/* Shared @mention helpers and textarea dropdown. */
(function () {
  const { useEffect, useMemo, useRef, useState } = React;

  const MAX_MENTION_OPTIONS = 10;
  const MENTION_MATCH = /(^|[\s([{])@([^\n@]*)$/;

  function cleanLabel(value, fallback) {
    return String(value || fallback || "").trim();
  }

  function mentionPart(value, fallback) {
    const text = cleanLabel(value, fallback)
      .replace(/[@.]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text || fallback || "Item";
  }

  function mentionDisplayValue(parts) {
    return `@${parts.map((part, index) => mentionPart(part, `ITEM_${index + 1}`)).join(".")}`;
  }

  function mentionIdentityPart(type, id) {
    const cleanType = String(type || "").trim().replace(/[^\w-]+/g, "");
    const cleanId = String(id || "").trim().replace(/[{}\s\]]+/g, "");
    return cleanType && cleanId ? `${cleanType}:${cleanId}` : "";
  }

  function mentionValue(parts, identities) {
    const displayValue = mentionDisplayValue(parts);
    const suffix = (identities || [])
      .map((identity) => mentionIdentityPart(identity && identity.type, identity && identity.id))
      .filter(Boolean)
      .join(";");
    return suffix ? `${displayValue}[${suffix}]` : displayValue;
  }

  function documentMentionName(document) {
    return cleanLabel(document && (document.title || document.name || document.fileName), "Document").replace(/\.[a-z0-9]{1,8}$/i, "");
  }

  function addOption(options, seen, option) {
    if (!option || !option.value || seen.has(option.value)) return;
    seen.add(option.value);
    options.push(option);
  }

  function sortedByLabel(items) {
    return (items || []).slice().sort((a, b) => cleanLabel(a.name || a.title || a.fileName || a.email, "").localeCompare(cleanLabel(b.name || b.title || b.fileName || b.email, "")));
  }

  function projectTickets(project) {
    const tickets = [];
    (project && project.workPackages || []).forEach((workPackage) => {
      (workPackage.ticketClusters || []).forEach((cluster) => {
        (cluster.tickets || []).forEach((ticket) => {
          tickets.push({ ...ticket, workPackage, cluster });
        });
      });
    });
    return tickets;
  }

  function buildMentionOptions(db) {
    const options = [];
    const seen = new Set();
    sortedByLabel(db && db.projects).forEach((project) => {
      const projectName = cleanLabel(project.name, "Project");
      const projectMention = mentionDisplayValue([projectName]);
      addOption(options, seen, {
        value: mentionValue([projectName], [{ type: "project", id: project.id }]),
        displayValue: projectMention,
        entityType: "project",
        entityId: project.id || "",
        label: projectName,
        detail: "Project",
        icon: "projects",
        search: `${projectMention} ${projectName} project`,
      });
      sortedByLabel(project.documents).forEach((document) => {
        const documentName = documentMentionName(document);
        const displayValue = mentionDisplayValue([projectName, documentName]);
        addOption(options, seen, {
          value: mentionValue([projectName, documentName], [{ type: "project", id: project.id }, { type: "document", id: document.id }]),
          displayValue,
          entityType: "document",
          entityId: document.id || "",
          label: documentName,
          detail: `${projectName} / Document`,
          icon: "doc",
          search: `${displayValue} ${projectName} ${documentName} document ${document.id || ""}`,
        });
      });
      sortedByLabel(projectTickets(project)).forEach((ticket) => {
        const ticketTitle = cleanLabel(ticket.title, "Ticket");
        const displayValue = mentionDisplayValue([projectName, ticketTitle]);
        addOption(options, seen, {
          value: mentionValue([projectName, ticketTitle], [{ type: "project", id: project.id }, { type: "ticket", id: ticket.id }]),
          displayValue,
          entityType: "ticket",
          entityId: ticket.id || "",
          label: ticketTitle,
          detail: `${projectName} / Ticket`,
          icon: "tag",
          search: `${displayValue} ${projectName} ${ticketTitle} ticket ${ticket.id || ""} ${(ticket.workPackage && ticket.workPackage.name) || ""} ${(ticket.cluster && ticket.cluster.name) || ""}`,
        });
      });
    });
    sortedByLabel(db && db.customers).forEach((customer) => {
      const customerName = cleanLabel(customer.name, "Customer");
      const customerMention = mentionDisplayValue([customerName]);
      addOption(options, seen, {
        value: mentionValue([customerName], [{ type: "customer", id: customer.id }]),
        displayValue: customerMention,
        entityType: "customer",
        entityId: customer.id || "",
        label: customerName,
        detail: "Customer",
        icon: "customers",
        search: `${customerMention} ${customerName} customer`,
      });
      sortedByLabel(customer.contactPersons).forEach((contact) => {
        const contactName = cleanLabel(contact.name || contact.email, "Contact");
        const displayValue = mentionDisplayValue([customerName, contactName]);
        addOption(options, seen, {
          value: mentionValue([customerName, contactName], [{ type: "customer", id: customer.id }, { type: "contact", id: contact.id }]),
          displayValue,
          entityType: "contact",
          entityId: contact.id || "",
          label: contactName,
          detail: `${customerName} / Contact`,
          icon: "users",
          search: `${displayValue} ${customerName} ${contactName} ${contact.email || ""} ${contact.id || ""} contact`,
        });
      });
    });
    return options;
  }

  function mentionQueryAt(value, caretIndex) {
    const cursor = Number.isFinite(caretIndex) ? caretIndex : String(value || "").length;
    const beforeCursor = String(value || "").slice(0, cursor);
    const match = beforeCursor.match(MENTION_MATCH);
    if (!match) return null;
    const query = match[2] || "";
    return { at: beforeCursor.length - query.length - 1, end: cursor, query };
  }

  function mentionScore(option, query) {
    const q = String(query || "").trim().toUpperCase();
    if (!q) return 1;
    const value = String(option.displayValue || option.value || "").toUpperCase().replace(/^@/, "");
    const search = String(option.search || "").toUpperCase();
    if (value === q) return 100;
    if (value.startsWith(q)) return 90;
    if (value.includes(q)) return 70;
    if (search.includes(q.replace(/[._]+/g, " "))) return 50;
    return 0;
  }

  function filterMentionOptions(options, query) {
    return (options || [])
      .map((option) => ({ option, score: mentionScore(option, query) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || a.option.value.localeCompare(b.option.value))
      .slice(0, MAX_MENTION_OPTIONS)
      .map((item) => item.option);
  }

  function serializeNode(node) {
    if (!node) return "";
    if (node.nodeType === 3) return node.nodeValue || "";
    if (node.nodeType === 11) return Array.from(node.childNodes || []).map(serializeNode).join("");
    if (node.nodeType !== 1) return "";
    if (node.classList && node.classList.contains("mention-pill")) return node.dataset.value || "";
    if (node.tagName === "BR") return "\n";
    const text = Array.from(node.childNodes || []).map(serializeNode).join("");
    return node.tagName === "DIV" || node.tagName === "P" ? `${text}\n` : text;
  }

  function serializeEditor(node) {
    return Array.from((node && node.childNodes) || []).map(serializeNode).join("").replace(/\n$/, "");
  }

  function caretOffset(editor) {
    const selection = window.getSelection && window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editor.contains(selection.anchorNode)) return serializeEditor(editor).length;
    const range = selection.getRangeAt(0).cloneRange();
    range.selectNodeContents(editor);
    range.setEnd(selection.anchorNode, selection.anchorOffset);
    return serializeNode(range.cloneContents()).length;
  }

  function createMentionPill(option) {
    const pill = document.createElement("span");
    pill.className = "mention-pill";
    pill.contentEditable = "false";
    pill.dataset.value = option.value || "";
    pill.dataset.entityId = option.entityId || "";
    pill.dataset.entityType = option.entityType || "";
    pill.dataset.icon = option.icon || "tag";

    const label = document.createElement("span");
    label.className = "mention-pill-label";
    label.textContent = option.displayValue || option.value || "";

    const remove = document.createElement("button");
    remove.type = "button";
    remove.tabIndex = -1;
    remove.className = "mention-pill-remove";
    remove.setAttribute("aria-label", `Remove ${option.displayValue || option.value || "mention"}`);
    remove.textContent = "x";

    pill.appendChild(label);
    pill.appendChild(remove);
    return pill;
  }

  function findNextMention(text, start, options) {
    let best = null;
    (options || []).forEach((option) => {
      if (!option || !option.value) return;
      [option.value, option.displayValue].filter(Boolean).forEach((candidate) => {
        const index = text.indexOf(candidate, start);
        if (index < 0) return;
        if (!best || index < best.index || (index === best.index && candidate.length > best.match.length)) {
          best = { index, option, match: candidate };
        }
      });
    });
    return best;
  }

  function renderEditorValue(editor, text, options) {
    if (!editor) return;
    editor.textContent = "";
    const source = String(text || "");
    let index = 0;
    while (index < source.length) {
      const next = findNextMention(source, index, options);
      if (!next) {
        editor.appendChild(document.createTextNode(source.slice(index)));
        break;
      }
      if (next.index > index) editor.appendChild(document.createTextNode(source.slice(index, next.index)));
      editor.appendChild(createMentionPill(next.option));
      index = next.index + next.match.length;
    }
  }

  function setCaretAtOffset(editor, targetOffset) {
    const range = document.createRange();
    const selection = window.getSelection && window.getSelection();
    if (!selection) return;
    let current = 0;
    let placed = false;
    const visit = (node) => {
      if (!node || placed) return;
      if (node.nodeType === 3) {
        const length = (node.nodeValue || "").length;
        if (current + length >= targetOffset) {
          range.setStart(node, Math.max(0, targetOffset - current));
          placed = true;
          return;
        }
        current += length;
        return;
      }
      if (node.nodeType !== 1 && node.nodeType !== 11) return;
      if (node.classList && node.classList.contains("mention-pill")) {
        const length = (node.dataset.value || "").length;
        if (current + length >= targetOffset) {
          range.setStartAfter(node);
          placed = true;
          return;
        }
        current += length;
        return;
      }
      Array.from(node.childNodes || []).forEach(visit);
    };
    visit(editor);
    if (!placed) range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function MentionTextarea({ value, onValueChange, mentionOptions, textareaRef, className, rows, placeholder, disabled, autoFocus, onKeyDown, id, "data-appligator-id": dataAppligatorId }) {
    const ownRef = useRef(null);
    const blurTimerRef = useRef(null);
    const latestValueRef = useRef(String(value || ""));
    const [activeQuery, setActiveQuery] = useState(null);
    const [highlighted, setHighlighted] = useState(0);
    const [menuStyle, setMenuStyle] = useState(null);
    const options = useMemo(() => filterMentionOptions(mentionOptions, activeQuery && activeQuery.query), [mentionOptions, activeQuery && activeQuery.query]);
    const open = !!activeQuery && options.length > 0 && !disabled;

    function positionMenu() {
      const node = ownRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewportH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const viewportW = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      const left = Math.max(8, Math.min(rect.left, viewportW - 280));
      const width = Math.max(260, Math.min(rect.width, viewportW - left - 8));
      setMenuStyle({
        left: `${left}px`,
        top: `${rect.bottom + 6}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(120, viewportH - rect.bottom - 14)}px`,
      });
    }

    function assignRef(node) {
      ownRef.current = node;
      if (typeof textareaRef === "function") textareaRef(node);
      else if (textareaRef) textareaRef.current = node;
    }

    function updateActive(node) {
      const serialized = serializeEditor(node);
      const offset = caretOffset(node);
      const next = mentionQueryAt(serialized, offset);
      setActiveQuery(next);
      setHighlighted(0);
    }

    function commitValue(node, event) {
      const nextValue = serializeEditor(node);
      latestValueRef.current = nextValue;
      if (onValueChange) onValueChange(nextValue, node, event);
      updateActive(node);
    }

    function insertMention(option) {
      const node = ownRef.current;
      if (!node || !activeQuery || !option) return;
      const current = serializeEditor(node);
      const next = `${current.slice(0, activeQuery.at)}${option.value} ${current.slice(activeQuery.end)}`;
      const nextCaret = activeQuery.at + option.value.length + 1;
      latestValueRef.current = next;
      renderEditorValue(node, next, mentionOptions);
      if (onValueChange) onValueChange(next, node, null);
      setActiveQuery(null);
      window.requestAnimationFrame(() => {
        node.focus();
        setCaretAtOffset(node, nextCaret);
      });
    }

    function handleKeyDown(event) {
      if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        event.preventDefault();
        setHighlighted((current) => (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
        return;
      }
      if (open && (event.key === "Enter" || event.key === "Tab")) {
        event.preventDefault();
        insertMention(options[highlighted] || options[0]);
        return;
      }
      if (open && event.key === "Escape") {
        event.preventDefault();
        setActiveQuery(null);
        return;
      }
      if (onKeyDown) onKeyDown(event);
    }

    function handleMouseDown(event) {
      const button = event.target && event.target.closest && event.target.closest(".mention-pill-remove");
      if (!button) return;
      event.preventDefault();
      const pill = button.closest(".mention-pill");
      const node = ownRef.current;
      if (!pill || !node) return;
      const after = document.createTextNode("");
      pill.replaceWith(after);
      latestValueRef.current = serializeEditor(node);
      if (onValueChange) onValueChange(latestValueRef.current, node, null);
      window.requestAnimationFrame(() => {
        node.focus();
        const range = document.createRange();
        const selection = window.getSelection && window.getSelection();
        if (!selection) return;
        range.setStart(after, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        updateActive(node);
      });
    }

    useEffect(() => () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    }, []);

    useEffect(() => {
      if (autoFocus && ownRef.current) ownRef.current.focus();
    }, []);

    useEffect(() => {
      const node = ownRef.current;
      const nextValue = String(value || "");
      if (!node || serializeEditor(node) === nextValue) return;
      latestValueRef.current = nextValue;
      renderEditorValue(node, nextValue, mentionOptions);
    }, [value, mentionOptions]);

    useEffect(() => {
      if (!open) {
        setMenuStyle(null);
        return undefined;
      }
      positionMenu();
      window.addEventListener("scroll", positionMenu, true);
      window.addEventListener("resize", positionMenu);
      if (window.visualViewport) window.visualViewport.addEventListener("resize", positionMenu);
      return () => {
        window.removeEventListener("scroll", positionMenu, true);
        window.removeEventListener("resize", positionMenu);
        if (window.visualViewport) window.visualViewport.removeEventListener("resize", positionMenu);
      };
    }, [open, options.length]);

    const menu = open && menuStyle ? (
      <div className="mention-menu" style={menuStyle} role="listbox" data-appligator-id="mention-dropdown">
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            className={index === highlighted ? "selected" : ""}
            role="option"
            aria-selected={index === highlighted}
            onMouseDown={(event) => {
              event.preventDefault();
              insertMention(option);
            }}
          >
            <Icon name={option.icon || "tag"} size={13} />
            <span>
              <strong>{option.displayValue || option.value}</strong>
              <em>{option.detail}</em>
            </span>
          </button>
        ))}
      </div>
    ) : null;

    return (
      <div className="mention-textarea-wrap">
        <div
          id={id}
          ref={assignRef}
          className={`mention-editor ${className || ""}`}
          role="textbox"
          aria-multiline="true"
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          data-rows={rows || ""}
          data-appligator-id={dataAppligatorId}
          onInput={(event) => commitValue(event.currentTarget, event)}
          onMouseDown={handleMouseDown}
          onClick={(event) => updateActive(event.currentTarget)}
          onKeyUp={(event) => updateActive(event.currentTarget)}
          onKeyDown={handleKeyDown}
          onBlur={() => { blurTimerRef.current = setTimeout(() => setActiveQuery(null), 120); }}
          onFocus={(event) => updateActive(event.currentTarget)}
        />
        {menu && ReactDOM.createPortal(menu, document.body)}
      </div>
    );
  }

  Object.assign(window, {
    MentionTextarea,
    buildMentionOptions,
    filterMentionOptions,
    mentionQueryAt,
  });
})();
